import { useEffect, useState } from "react";
import { api, fmtErr, fmtDateTime, fmtSize, API } from "@/lib/api";
import { ROLE_LABELS } from "@/components/badges";
import { Thumb } from "@/components/common";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { EyeOff, FileText, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { toast } from "sonner";

const EDIT_WINDOW_MS = 30000;

export default function ChatThread({ messages, files = [], currentUserId, ticketId, onEdited }) {
  const [now, setNow] = useState(Date.now());
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!messages?.length) {
    return <div className="rounded-sm border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Aucun message pour le moment.</div>;
  }

  const canEdit = (m) =>
    !!currentUserId && m.author_id === currentUserId && m.kind !== "system" &&
    (now - new Date(m.created_at).getTime()) <= EDIT_WINDOW_MS;

  const startEdit = (m) => { setEditingId(m.id); setDraft(m.content); };
  const cancelEdit = () => { setEditingId(null); setDraft(""); };
  const saveEdit = async (m) => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await api.patch(`/tickets/${ticketId}/messages/${m.id}`, { content: draft });
      toast.success("Message modifié");
      setEditingId(null);
      setDraft("");
      onEdited?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="chat-thread">
      {messages.map((m, i) => {
        if (m.kind === "system") {
          return (
            <motion.div key={m.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
              className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`chat-message-system-${m.id}`}>
              <div className="h-px min-w-4 flex-1 bg-border" />
              <div className="text-center text-xs text-muted-foreground">{m.content} · {fmtDateTime(m.created_at)}</div>
              <div className="h-px min-w-4 flex-1 bg-border" />
            </motion.div>
          );
        }
        const isClient = m.kind === "client";
        const isInternal = m.kind === "internal";
        const editable = canEdit(m);
        return (
          <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className={cn("flex", isClient ? "justify-start" : "justify-end")}
            data-testid={`chat-message-${m.kind}-${m.id}`}>
            <div className={cn(
              "min-w-0 max-w-[85%] sm:max-w-[70%] rounded-sm border p-4",
              isInternal
                ? "border-[hsl(var(--note-border))] bg-[hsl(var(--note-bg))]"
                : isClient
                  ? "border-border bg-muted/40"
                  : "border-l-4 border-l-emerald-500 border-y-emerald-100 border-r-emerald-100 bg-emerald-50/80 text-emerald-950"
            )}>
              <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                {isInternal && <EyeOff className="h-3.5 w-3.5 shrink-0 text-primary" />}
                <span className="font-semibold">{m.author_name}</span>
                <span className="text-muted-foreground">{ROLE_LABELS[m.author_role] || m.author_role}</span>
                {isInternal && <span className="font-semibold text-primary uppercase tracking-wider">Note interne</span>}
                <span className="ml-auto shrink-0 text-muted-foreground">
                  {fmtDateTime(m.created_at)}{m.edited_at ? " · modifié" : ""}
                </span>
              </div>
              {editingId === m.id ? (
                <div className="space-y-2" data-testid={`chat-edit-box-${m.id}`}>
                  <Textarea rows={3} value={draft} onChange={(e) => setDraft(e.target.value)}
                    className="rounded-sm text-sm" data-testid={`chat-edit-input-${m.id}`} />
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" className="rounded-sm bg-primary text-white hover:bg-red-700"
                      disabled={saving || !draft.trim()} onClick={() => saveEdit(m)} data-testid={`chat-edit-save-${m.id}`}>
                      {saving ? "Enregistrement…" : "Enregistrer"}
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="rounded-sm"
                      onClick={cancelEdit} data-testid={`chat-edit-cancel-${m.id}`}>Annuler</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{m.content}</p>
                  {editable && (
                    <button type="button" onClick={() => startEdit(m)} data-testid={`chat-edit-button-${m.id}`}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                      <Pencil className="h-3 w-3" /> Modifier
                    </button>
                  )}
                </>
              )}
              {files.filter((f) => f.message_id === m.id).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 border-t border-border/60 pt-3">
                  {files.filter((f) => f.message_id === m.id).map((f) => (
                    f.mime?.startsWith("image/") ? (
                      <Thumb key={f.id} file={f} testid={`msg-file-${f.id}`} />
                    ) : (
                      <a key={f.id} href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" data-testid={`msg-file-${f.id}`}
                        className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-accent transition-colors">
                        <FileText className="h-3.5 w-3.5 text-primary" />
                        <span className="max-w-40 truncate font-medium">{f.original_name}</span>
                        <span className="text-muted-foreground">{fmtSize(f.size)}</span>
                      </a>
                    )
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
